import { getRiders,createRider,uploadImage,updateRider, DeleteUser, deleteUserandUpdate } from "../../Utils/apiServices";
import { useState, useEffect, useMemo } from "react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  Typography,
  Stack,
  InputAdornment,
  Chip,
} from '@mui/material';
import './Riders.css';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { calculateAge } from "../../Utils/helper";
import ConfirmDialog from "../../Components/ConfirmDialog";

/** API may send boolean, 0/1, or string "true"/"false"; also support camelCase key. */
function parseSessionalPass(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (v === 'true' || v === '1') return true;
    if (v === 'false' || v === '0' || v === '') return false;
  }
  return false;
}

function getSessionalPassFromRider(rider) {
  if (!rider || typeof rider !== 'object') return false;
  const raw =
    rider.as_sessional_pass ??
    rider.as_session_pass ??
    rider.asSessionalPass;
  return parseSessionalPass(raw);
}

function getAsPlusFromRider(rider) {
  if (!rider || typeof rider !== 'object') return false;
  const raw = rider.as_plus ?? rider.asPlus;
  return parseSessionalPass(raw);
}

function dateInputValueFromRider(isoOrDate) {
  if (isoOrDate == null || isoOrDate === '') return '';
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
}

function formatRiderPassDate(value) {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// createRider

const Riders = () => {
  const [riders, setRiders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUserType, setFilterUserType] = useState('all');
  const [filterSessional, setFilterSessional] = useState('all');
  const [filterAsPlus, setFilterAsPlus] = useState('all');
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [newRider, setNewRider] = useState({
    name: null,
    user_id: null,
    dob: null,
    user_type: 'Rider',
    email: null,
    profile_picture: null,
    as_sessional_pass: false,
    as_plus: false,
    as_plus_date: '',
    as_plus_end_date: '',
    sessional_pass_end_date: '',
    sessional_plus_date: '',
  });
  const [convertToRelation, setConvertToRelation] = useState(false);
  const [relatedRiderId, setRelatedRiderId] = useState('');
  const [migrateDialogOpen, setMigrateDialogOpen] = useState(false);
  const [migrateData, setMigrateData] = useState({
    IdtoDelete: '',
    IdtoUpdate: ''
  });
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    riderId: null,
    deleteCompleteUser: null,
  });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getRiders().then((response) => {
      const list = response?.data?.data ?? response?.data;
      const rows = Array.isArray(list) ? list : [];
      setRiders(rows);
    });
  }, []);

  const userTypeOptions = useMemo(() => {
    const types = new Set();
    riders.forEach((r) => {
      if (r.user_type) types.add(r.user_type);
    });
    return Array.from(types).sort();
  }, [riders]);

  const filteredRiders = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return riders.filter((rider) => {
      if (term) {
        const matchesSearch =
          (rider.name?.toLowerCase() || '').includes(term) ||
          (rider.user_id?.toLowerCase() || '').includes(term) ||
          (rider.email?.toLowerCase() || '').includes(term);
        if (!matchesSearch) return false;
      }
      if (filterUserType !== 'all' && (rider.user_type || '') !== filterUserType) {
        return false;
      }
      if (filterSessional !== 'all') {
        const hasSessional = getSessionalPassFromRider(rider);
        if (filterSessional === 'yes' && !hasSessional) return false;
        if (filterSessional === 'no' && hasSessional) return false;
      }
      if (filterAsPlus !== 'all') {
        const hasPlus = getAsPlusFromRider(rider);
        if (filterAsPlus === 'yes' && !hasPlus) return false;
        if (filterAsPlus === 'no' && hasPlus) return false;
      }
      return true;
    });
  }, [riders, searchTerm, filterUserType, filterSessional, filterAsPlus]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearFilters = () => {
    setFilterUserType('all');
    setFilterSessional('all');
    setFilterAsPlus('all');
    setSearchTerm('');
  };

  const handleEdit = (rider) => {
    setIsEditing(true);
    setConvertToRelation(false);
    setRelatedRiderId('');
    setNewRider({
      ...rider,
      dob: rider.dob ? new Date(rider.dob) : null,
      as_sessional_pass: getSessionalPassFromRider(rider),
      as_plus: getAsPlusFromRider(rider),
      as_plus_date: dateInputValueFromRider(rider.as_plus_date),
      as_plus_end_date: dateInputValueFromRider(rider.as_plus_end_date),
      sessional_pass_end_date: dateInputValueFromRider(rider.sessional_pass_end_date),
      sessional_plus_date: dateInputValueFromRider(rider.sessional_plus_date),
    });
    handleOpen();
  };

  const openDeleteRiderConfirm = (riderId, deleteCompleteUser) => {
    setDeleteConfirm({ open: true, riderId, deleteCompleteUser });
  };

  const closeDeleteRiderConfirm = () => {
    if (deleteLoading) return;
    setDeleteConfirm({ open: false, riderId: null, deleteCompleteUser: null });
  };

  const confirmDeleteRider = () => {
    const { riderId, deleteCompleteUser } = deleteConfirm;
    if (riderId == null) return;
    setDeleteLoading(true);
    DeleteUser({ id: riderId, deleteCompleteUser })
      .then(() => getRiders())
      .then((response) => {
        const list = response?.data?.data ?? response?.data;
        const rows = Array.isArray(list) ? list : [];
        setRiders(rows);
        setSnackbar({
          open: true,
          message: `Rider successfully deleted ${deleteCompleteUser ? 'with' : 'without'} records`,
          severity: 'success'
        });
        setDeleteConfirm({ open: false, riderId: null, deleteCompleteUser: null });
      })
      .catch((error) => {
        console.error('Error deleting rider:', error);
        setSnackbar({
          open: true,
          message: 'Error deleting rider',
          severity: 'error'
        });
      })
      .finally(() => setDeleteLoading(false));
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setIsEditing(false);
    setConvertToRelation(false);
    setRelatedRiderId('');
    setNewRider({
      name: '',
      user_id: '',
      dob: null,
      user_type: 'Rider',
      email: '',
      profile_picture: '',
      as_sessional_pass: false,
      as_plus: false,
      as_plus_date: '',
      as_plus_end_date: '',
      sessional_pass_end_date: '',
      sessional_plus_date: '',
    });
  };

  const handleChange = (e) => {
    setNewRider({
      ...newRider,
      [e.target.name]: e.target.value
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];

    const formData = new FormData();
    formData.append('file', file);
    const response = await uploadImage(formData);
    setNewRider({
      ...newRider,
      profile_picture: response.data.file[0]
    });
  };

  const handleSubmit = async () => {
    try {
      const riderData = {
        id: newRider.id,
        name: newRider.name,
        user_id: newRider.user_id,
        dob: newRider.dob,
        user_type: convertToRelation ? 'Rider_Relation' : newRider.user_type,
        email: newRider.email,
        profile_picture: newRider.profile_picture,
        related_rider_id: convertToRelation && relatedRiderId ? relatedRiderId : null
      };

      if (
        isEditing &&
        newRider.user_type === 'Rider' &&
        !convertToRelation
      ) {
        riderData.as_sessional_pass = !!newRider.as_sessional_pass;
        riderData.as_plus = !!newRider.as_plus;
        riderData.as_plus_date = newRider.as_plus_date?.trim() || null;
        riderData.as_plus_end_date = newRider.as_plus_end_date?.trim() || null;
        riderData.sessional_pass_end_date =
          newRider.sessional_pass_end_date?.trim() || null;
        riderData.sessional_plus_date =
          newRider.sessional_plus_date?.trim() || null;
      }

      if (isEditing) {
        await updateRider(riderData);
      } else {
        await createRider(riderData);
      }
      
      const updatedRiders = await getRiders();
      const list = updatedRiders?.data?.data ?? updatedRiders?.data;
      const rows = Array.isArray(list) ? list : [];
      setRiders(rows);
      handleClose();
    } catch (error) {
      console.error('Error saving rider:', error);
    }
  };

  const handleNameClick = (riderId) => {
    navigate(`/riders/details/${riderId}`);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleMigrateOpen = () => setMigrateDialogOpen(true);
  const handleMigrateClose = () => {
    setMigrateDialogOpen(false);
    setMigrateData({ IdtoDelete: '', IdtoUpdate: '' });
  };

  const handleMigrateChange = (e) => {
    setMigrateData({
      ...migrateData,
      [e.target.name]: e.target.value
    });
  };

  const handleMigrateSubmit = async () => {
    try {
      await deleteUserandUpdate(migrateData);
      setSnackbar({
        open: true,
        message: 'User migration completed successfully',
        severity: 'success'
      });
      handleMigrateClose();
      
      // Refresh riders data
      const updatedRiders = await getRiders();
      const list = updatedRiders?.data?.data ?? updatedRiders?.data;
      const rows = Array.isArray(list) ? list : [];
      setRiders(rows);
    } catch (error) {
      console.error('Error migrating user:', error);
      setSnackbar({
        open: true,
        message: 'Error migrating user',
        severity: 'error'
      });
    }
  };

  return (
    <Box>
      {/* Colorful page banner */}
      <Box
        sx={{
          mb: 3,
          p: 2.5,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 50%, #8b5cf6 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 8px 24px rgba(99,102,241,0.3)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -40,
            right: -40,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          },
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.3 }}>
            🏇 Riders Management
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            Search, filter and manage all riders — edit sessional pass and AS Plus details inline
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1 }}>{riders.length}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Total Riders</Typography>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
        >
          <TextField
            placeholder="Search by name, user ID, or email..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearch}
            fullWidth
            sx={{ maxWidth: { md: 420 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexShrink={0}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleMigrateOpen}
              startIcon={<SwapHorizIcon />}
              sx={{ borderColor: 'divider', color: 'text.primary' }}
            >
              Migrate result
            </Button>
            <Button variant="contained" onClick={handleOpen} startIcon={<PersonAddOutlinedIcon />}>
              Add rider
            </Button>
          </Stack>
        </Stack>

        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Filters
          </Typography>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'flex-end' }}
            flexWrap="wrap"
            useFlexGap
          >
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel id="rider-filter-user-type">User type</InputLabel>
              <Select
                labelId="rider-filter-user-type"
                label="User type"
                value={filterUserType}
                onChange={(e) => setFilterUserType(e.target.value)}
              >
                <MenuItem value="all">All types</MenuItem>
                {userTypeOptions.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel id="rider-filter-sessional">Sessional pass</InputLabel>
              <Select
                labelId="rider-filter-sessional"
                label="Sessional pass"
                value={filterSessional}
                onChange={(e) => setFilterSessional(e.target.value)}
              >
                <MenuItem value="all">Any</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel id="rider-filter-as-plus">AS Plus</InputLabel>
              <Select
                labelId="rider-filter-as-plus"
                label="AS Plus"
                value={filterAsPlus}
                onChange={(e) => setFilterAsPlus(e.target.value)}
              >
                <MenuItem value="all">Any</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              sx={{ alignSelf: { xs: 'stretch', md: 'center' }, height: 40 }}
            >
              Clear search and filters
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Showing {filteredRiders.length} of {riders.length} riders
          </Typography>
        </Box>
      </Paper>

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Delete rider"
        message={
          deleteConfirm.deleteCompleteUser
            ? 'Delete this rider and all associated records? This cannot be undone.'
            : 'Delete this rider without removing historical records? This cannot be undone.'
        }
        confirmLabel="Delete"
        onCancel={closeDeleteRiderConfirm}
        onConfirm={confirmDeleteRider}
        loading={deleteLoading}
      />

      <Dialog open={migrateDialogOpen} onClose={handleMigrateClose}>
        <DialogTitle>Migrate User Result</DialogTitle>
        <DialogContent>
          <TextField
            name="IdtoDelete"
            label="ID to Delete"
            fullWidth
            margin="dense"
            value={migrateData.IdtoDelete}
            onChange={handleMigrateChange}
            placeholder="Enter ID to delete"
          />
          <TextField
            name="IdtoUpdate"
            label="ID to Migrate"
            fullWidth
            margin="dense"
            value={migrateData.IdtoUpdate}
            onChange={handleMigrateChange}
            placeholder="Enter ID to migrate"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMigrateClose}>Cancel</Button>
          <Button 
            onClick={handleMigrateSubmit} 
            variant="contained"
            color="secondary"
          >
            Migrate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{isEditing ? 'Edit Rider' : 'Create New Rider'}</DialogTitle>
        <DialogContent>
          {isEditing && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Convert To</InputLabel>
              <Select
                value={convertToRelation ? 'relation' : 'rider'}
                onChange={(e) => setConvertToRelation(e.target.value === 'relation')}
                label="Convert To"
              >
                <MenuItem value="rider">Keep as Rider</MenuItem>
                <MenuItem value="relation">Convert to Rider Relation</MenuItem>
              </Select>
            </FormControl>
          )}

          {convertToRelation && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Related Rider</InputLabel>
              <Select
                value={relatedRiderId}
                onChange={(e) => setRelatedRiderId(e.target.value)}
                label="Related Rider"
              >
                <MenuItem value="">None</MenuItem>
                {riders
                  .filter(r => r.user_type === 'Rider' && r.id !== newRider.id)
                  .map(rider => (
                    <MenuItem key={rider.id} value={rider.id}>
                      {rider.name} ({rider.user_id})
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          )}

          {isEditing && newRider.user_type === 'Rider' && !convertToRelation && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={parseSessionalPass(newRider.as_sessional_pass)}
                    onChange={(e) =>
                      setNewRider({
                        ...newRider,
                        as_sessional_pass: e.target.checked
                      })
                    }
                  />
                }
                label="Sessional pass"
                sx={{ mt: 1, display: 'block' }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={parseSessionalPass(newRider.as_plus)}
                    onChange={(e) =>
                      setNewRider({
                        ...newRider,
                        as_plus: e.target.checked
                      })
                    }
                  />
                }
                label="AS Plus"
                sx={{ display: 'block' }}
              />
              <TextField
                name="as_plus_date"
                label="AS Plus start"
                type="date"
                fullWidth
                margin="dense"
                value={newRider.as_plus_date || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                name="as_plus_end_date"
                label="AS Plus end"
                type="date"
                fullWidth
                margin="dense"
                value={newRider.as_plus_end_date || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                name="sessional_pass_end_date"
                label="Sessional pass end"
                type="date"
                fullWidth
                margin="dense"
                value={newRider.sessional_pass_end_date || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                name="sessional_plus_date"
                label="Sessional plus date"
                type="date"
                fullWidth
                margin="dense"
                value={newRider.sessional_plus_date || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}

          <TextField
            name="user_id"
            label="User ID"
            fullWidth
            margin="dense"
            value={newRider.user_id}
            onChange={handleChange}
          />
          <TextField
            name="dob"
            label="Date of Birth"
            type="date"
            fullWidth
            margin="dense"
            value={newRider.dob ? new Date(newRider.dob).toISOString().split('T')[0] : ''}
            onChange={(e) => {
              setNewRider({
                ...newRider,
                dob: new Date(e.target.value)
              });
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            name="name"
            label="Name"
            fullWidth
            margin="dense"
            value={newRider.name}
            onChange={handleChange}
          />
          <TextField
            name="email"
            label="Email"
            fullWidth
            margin="dense"
            value={newRider.email}
            onChange={handleChange}
          />
          <TextField
            name="profile_picture"
            label="Profile Picture URL"
            fullWidth
            margin="dense"
            type="file"
            onChange={handleImageChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          maxHeight: 'min(70vh, 720px)',
          overflow: 'auto',
        }}
      >
        <Table stickyHeader sx={{ minWidth: 1280 }} size="small">
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  color: 'text.secondary',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  letterSpacing: '0.02em',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  py: 1.5,
                  px: 1.25,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  verticalAlign: 'bottom',
                  lineHeight: 1.35,
                  backgroundColor: 'grey.50',
                },
              }}
            >
              <TableCell sx={{ minWidth: 56 }}>ID</TableCell>
              <TableCell sx={{ minWidth: 88 }}>User ID</TableCell>
              <TableCell sx={{ minWidth: 120 }}>Name</TableCell>
              <TableCell sx={{ minWidth: 52 }}>Age</TableCell>
              <TableCell sx={{ minWidth: 56 }}>Photo</TableCell>
              <TableCell sx={{ minWidth: 160 }}>Email</TableCell>
              <TableCell sx={{ minWidth: 100 }}>User type</TableCell>
              <TableCell sx={{ minWidth: 108 }}>Sessional pass</TableCell>
              <TableCell sx={{ minWidth: 80 }}>AS Plus</TableCell>
              <TableCell sx={{ minWidth: 132 }}>AS Plus start date</TableCell>
              <TableCell sx={{ minWidth: 128 }}>AS Plus end date</TableCell>
              <TableCell sx={{ minWidth: 150 }}>Sessional pass end date</TableCell>
              <TableCell sx={{ minWidth: 142 }}>Sessional plus date</TableCell>
              <TableCell align="right" sx={{ minWidth: 220 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRiders.map((rider) => {
              const sessional = getSessionalPassFromRider(rider);
              const asPlus = getAsPlusFromRider(rider);
              return (
              <TableRow
                key={rider.id}
                hover
                sx={{
                  '&:last-child td': { borderBottom: 0 },
                  transition: 'background-color 0.15s ease',
                }}
              >
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>{rider.id}</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>{rider.user_id}</TableCell>
                <TableCell>
                  <Button
                    onClick={() => handleNameClick(rider.id)}
                    sx={{
                      textTransform: 'none',
                      textAlign: 'left',
                      fontWeight: 600,
                      color: 'primary.main',
                      p: 0,
                      minWidth: 0,
                    }}
                  >
                    {rider.name}
                  </Button>
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>
                  {rider.dob ? calculateAge(rider.dob) : '—'}
                </TableCell>
                <TableCell>
                  <Box
                    component="img"
                    src={rider.profile_picture}
                    alt=""
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'grey.100',
                      display: 'block',
                    }}
                  />
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', maxWidth: 180 }}>{rider.email || '—'}</TableCell>
                <TableCell>
                  <Chip label={rider.user_type || '—'} size="small" variant="outlined" sx={{ fontWeight: 500 }} />
                </TableCell>
                <TableCell>
                  <Chip
                    label={sessional ? 'Yes' : 'No'}
                    size="small"
                    color={sessional ? 'success' : undefined}
                    variant={sessional ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={asPlus ? 'Yes' : 'No'}
                    size="small"
                    color={asPlus ? 'success' : undefined}
                    variant={asPlus ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                  {formatRiderPassDate(rider.as_plus_date)}
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                  {formatRiderPassDate(rider.as_plus_end_date)}
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                  {formatRiderPassDate(rider.sessional_pass_end_date)}
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                  {formatRiderPassDate(rider.sessional_plus_date)}
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                    <IconButton
                      onClick={() => handleEdit(rider)}
                      size="small"
                      color="primary"
                      sx={{ border: '1px solid', borderColor: 'divider' }}
                      aria-label="Edit rider"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => openDeleteRiderConfirm(rider.id, true)}
                    >
                      With records
                    </Button>
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      onClick={() => openDeleteRiderConfirm(rider.id, false)}
                    >
                      Without
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Riders;
